import { Button, SpinningLoader } from "risa-oasis-ui_v2";
import { SourceOrg } from "../../utils/copyOrgDxConfig";

interface DxConfigCopyPanelProps {
  sourceOrgs: SourceOrg[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onCopy: (sourceOrg: SourceOrg) => void;
  copyingOrgId: string | null;
}

/**
 * R4 "Copy + import" empty state. Shown instead of the DX code table when the
 * selected organization has no dx_code_config docs yet. Lists organizations
 * that already have a config so the user can clone one of them wholesale.
 */
const DxConfigCopyPanel: React.FC<DxConfigCopyPanelProps> = ({
  sourceOrgs,
  loading,
  error,
  onRetry,
  onCopy,
  copyingOrgId,
}) => {
  return (
    <div className="dx-config-copy-panel flex h-full items-start justify-center px-4 pt-8">
      <div className="w-full max-w-md rounded-md border border-primaryGray-14 bg-white p-6 shadow-sm">
        <div className="text-sm font-semibold leading-normal text-black">
          No DX code config found for this organization
        </div>
        <div className="mt-1 text-sm leading-normal text-primaryGray-6">
          Copy an existing organization&apos;s DX code config to get started.
          The copy includes all drug (J-code) configurations and the source
          organization&apos;s DX source policy.
        </div>

        {loading ? (
          <div className="mt-4 flex h-10 items-center gap-3">
            <span className="flex shrink-0 items-center [&_svg]:h-4 [&_svg]:w-4">
              <SpinningLoader />
            </span>
            <span className="text-sm text-primaryGray-6">
              Finding organizations with a DX code config…
            </span>
          </div>
        ) : error ? (
          <div className="mt-4 flex flex-col gap-3">
            <div className="text-sm text-[#BB0202]">{error}</div>
            <Button
              buttonType="secondary"
              size="medium"
              disabled={false}
              onClick={onRetry}
            >
              Retry
            </Button>
          </div>
        ) : sourceOrgs.length === 0 ? (
          <div className="mt-4 text-sm text-primaryGray-6">
            No other organization has a DX code config yet.
          </div>
        ) : (
          <div className="mt-4 flex flex-col">
            {sourceOrgs.map((org) => {
              const isCopying = copyingOrgId === org.id;
              return (
                <div
                  key={org.id}
                  className="flex items-center justify-between border-b border-[#e5e7eb] py-3 last:border-b-0"
                >
                  <span className="truncate text-sm font-normal text-black">
                    {org.name || org.id}
                  </span>
                  <Button
                    buttonType="secondary"
                    size="small"
                    disabled={copyingOrgId !== null}
                    onClick={() => onCopy(org)}
                  >
                    {isCopying ? "Copying…" : "Copy"}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DxConfigCopyPanel;
